import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaWifi, FaSwimmingPool, FaTv, FaUtensils, FaBed, FaShower } from "react-icons/fa";
import { DateRange } from "react-date-range";
import { addDays } from "date-fns";
import { FaStar, FaCalendarAlt, FaTimesCircle } from "react-icons/fa"; // removed FaPhoneAlt
import "../Styles/ListingPage.css";

const ListingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const property = location.state?.property;

  // POPUP STATES
  const [openDescPopup, setOpenDescPopup] = useState(false);
  const [openAmenitiesPopup, setOpenAmenitiesPopup] = useState(false);
  const [openHouseRulesPopup, setOpenHouseRulesPopup] = useState(false);

  // RIGHT SIDEBAR CALENDAR STATE
  const [openRightCalendar, setOpenRightCalendar] = useState(false); // ✅ added

  const amenityIcons = {
    "Wi-Fi": <FaWifi />,
    "Swimming pool": <FaSwimmingPool />,
    "TV": <FaTv />,
    "Kitchen": <FaUtensils />,
    "Bed": <FaBed />,
    "Shower": <FaShower />,
  };

  // FULL DESCRIPTION
  const fullDescription = `
Enquire now for relocator and long term stay discounts

Enjoy a stylish and comfortable stay at this centrally-located gem, perfect for both business and leisure. Thoughtfully designed with modern decor and cozy touches, you'll be just steps away from top attractions, restaurants, and transport links. Whether you're exploring the city or working remotely, this space offers the perfect blend of convenience and charm.

Enjoy a stylish and comfortable stay at this centrally located gem, perfect for both business and leisure. Thoughtfully designed with modern decor and cozy touches, this studio apartment at Hamilton Property offers everything you need for a relaxing and convenient stay in Dubai. Whether you're exploring the city or working remotely, this well-appointed space delivers the ideal blend of comfort, charm, and functionality.

🏡 The Space  
• King-size bed with satellite TV  
• Fully equipped kitchen  
• Fast Wi-Fi  
• Complimentary tea, coffee & toiletries  

🏡 Location  
• Beach – 20 mins  
• Shops – 2 mins  
• Bars – 5 mins  
• Metro – 15 mins  
• Mall of the Emirates – 10–15 mins  
• Airport – 25–30 mins  

🛋️ Relax & Recharge  
• Swimming pool  
• Gym  
• Kids area  

Extras available upon request: Cot, highchair, extra cleaning, etc.

House Rules  
• Check-in: 3pm  
• Check-out: 11am  
• No smoking  
• No pets  
`;

  // Calendar state
  const [range, setRange] = useState([
    {
      startDate: new Date(),
      endDate: addDays(new Date(), 3),
      key: "selection",
    },
  ]);

  useEffect(() => {
    if (!property) navigate("/all-listings");
  }, [property, navigate]); // ✅ added navigate to deps

  if (!property) return null;

  const nights =
    range[0]?.endDate && range[0]?.startDate
      ? (range[0].endDate - range[0].startDate) / (1000 * 60 * 60 * 24)
      : 0;

  return (
    <div className="listing-page">
      {/* GALLERY */}
      <section className="gallery-grid">
        <div className="gallery-left">
          <img src={property.images[0]} alt="Main" />
        </div>

        <div className="gallery-right">
          {property.images.slice(1, 5).map((img, i) => (
            <div key={i} className="right-img">
              <img src={img} alt={`img-${i}`} />
            </div>
          ))}
        </div>
      </section>

      <div className="listing-grid">
        <main className="listing-left">
          {/* TITLE */}
          <section className="title-section">
            <h1>{property.title}</h1>
            <div className="tags">
              {property.type} · {property.guests} guests · {property.bedrooms} bedrooms · {property.bathrooms} bathrooms
            </div>
            <div className="rating rating-under">
              <FaStar /> <span className="score">{property.rating.toFixed(2)}</span>
            </div>
            <p className="highlight-text">
              Enquire now for relocator and long term stay discounts
            </p>
            <p className="description-long">{property.description.slice(0, 180)}...</p>
            <button className="show-more" onClick={() => setOpenDescPopup(true)}>
              Show more
            </button>
          </section>

          <hr />

          {/* AMENITIES */}
          <section className="amenities-section">
            <h3>Amenities</h3>
            <ul className="amenities-grid">
              {property.amenities.slice(0, 6).map((a, i) => (
                <li key={i}>
                  <span className="amenity-icon">{amenityIcons[a] || <FaUtensils />}</span>
                  <span className="amenity-text">{a}</span>
                </li>
              ))}
            </ul>
            <button className="show-more" onClick={() => setOpenAmenitiesPopup(true)}>
              Show all amenities
            </button>
          </section>

          <hr />

          {/* CALENDAR */}
          <section className="availability-section">
            <h3>Available days</h3>
            <DateRange
              editableDateInputs={true}
              onChange={(item) => setRange([item.selection])}
              moveRangeOnFirstSelection={false}
              ranges={range}
              minDate={new Date()}
              className="calendar-element"
              months={2}
              direction="horizontal"
              showDateDisplay={false}
            />

            <div className="check-summary-row">
              <div className="check-dates">
                <strong>Check-in:</strong>{" "}
                {range[0].startDate ? range[0].startDate.toDateString() : "--"}
                {range[0].endDate && range[0].endDate > range[0].startDate && (
                  <>
                    &nbsp;|&nbsp; <strong>Check-out:</strong>{" "}
                    {range[0].endDate.toDateString()}
                  </>
                )}
              </div>

              <button
                className="clear-dates-btn"
                onClick={() =>
                  setRange([
                    {
                      startDate: addDays(new Date(), 0),
                      endDate: addDays(new Date(), -1),
                      key: "selection",
                    },
                  ])
                }
              >
                Clear Dates
              </button>
            </div>
          </section>

          <hr />

          {/* MAP */}
          <section className="map-section">
            <div className="map-box">Leaflet, © OpenStreetMap</div>
            <div className="map-note">Report a map error</div>
          </section>

          <hr />

          {/* GOOD TO KNOW */}
          <section className="good-to-know">
            <h3>Good to know</h3>

            <h4>House Rules</h4>
            <ul>
              <li>Check-in: 3 pm</li>
              <li>Check-out: 11 am</li>
              <li>Pets: not allowed</li>
              <li>Smoking inside: not allowed</li>
            </ul>

            <button
              className="show-more"
              onClick={() => setOpenHouseRulesPopup(true)}
            >
              Show more
            </button>

            <h4>Cancellation Policy</h4>
            <ul>
              <li>100% refund up to 14 days before arrival</li>
              <li>50% refund up to 7 days before arrival</li>
            </ul>
          </section>
        </main>

        {/* RIGHT SIDEBAR */}
        <aside className="listing-right">
          <div className="booking-card">
            <div className="price">
              AED {property?.priceTag?.toLocaleString() || "850"} / night
            </div>

            <div className="check-summary-row">
              <div className="check-dates" onClick={() => setOpenRightCalendar(prev => !prev)}>
                <FaCalendarAlt />
                {range[0].startDate.toDateString()} - {range[0].endDate.toDateString()}
              </div>
              <div>Guests: {property.guests}</div>
            </div>

            {openRightCalendar && (
              <DateRange
                editableDateInputs={true}
                onChange={(item) => setRange([item.selection])}
                moveRangeOnFirstSelection={false}
                ranges={range}
                minDate={new Date()}
                className="calendar-element"
                months={2}
                direction="horizontal"
                showDateDisplay={false}
              />
            )}

            <div className="price-breakdown">
              <div className="row">
                <span>{`AED ${property?.priceTag?.toLocaleString() || "0"} X ${nights} nights`}</span>
                <span>AED {((property?.priceTag || 0) * nights).toLocaleString()}</span>
              </div>
              <div className="row">
                <span>Monthly discount</span>
                <span>-AED 3,226</span>
              </div>
              <div className="row">
                <span>Cleaning fee</span>
                <span>AED 300</span>
              </div>
              <div className="row">
                <span>City / Tourism tax</span>
                <span>AED 106</span>
              </div>
              <hr />
              <div className="row total">
                <span>Total</span>
                <span>
                  AED {((property.priceTag * nights) - 3226 + 300 + 106).toLocaleString()}
                </span>
              </div>
            </div>

            <div className="button-row">
              <button
                className="book-btn"
                onClick={() =>
                  navigate("/checkout", {
                    state: {
                      booking: {
                        title: property.title,
                        image: property.images[0],
                        pricePerNight: property.priceTag,
                        totalPrice: ((property.priceTag * nights) - 3226 + 300 + 106),
                        nights: nights,
                        guests: property.guests,
                        startDate: range[0].startDate,
                        endDate: range[0].endDate,
                      },
                    },
                  })
                }
              >
                Book Now
              </button>

              <button
                className="inquiry-btn"
                onClick={() =>
                  window.location.href = `mailto:info@wegohomes.com?subject=Inquiry about ${property.title}&body=Hello, I am interested in booking from ${range[0].startDate.toDateString()} to ${range[0].endDate.toDateString()}`
                }
              >
                Send Inquiry
              </button>
            </div>
          </div>
        </aside>
      </div>

      {/* POPUPS */}
      {openDescPopup && (
        <div className="popup-overlay">
          <div className="popup-box">
            <FaTimesCircle className="close-popup" onClick={() => setOpenDescPopup(false)} />
            <h2>About this property</h2>
            <div className="popup-scroll">
              <p>{fullDescription}</p>
            </div>
          </div>
        </div>
      )}

      {openAmenitiesPopup && (
        <div className="popup-overlay">
          <div className="popup-box">
            <FaTimesCircle className="close-popup" onClick={() => setOpenAmenitiesPopup(false)} />
            <h2>All Amenities</h2>
            <div className="popup-scroll">
              {/* Amenity groups here */}
            </div>
          </div>
        </div>
      )}

      {openHouseRulesPopup && (
        <div className="popup-overlay">
          <div className="popup-box">
            <FaTimesCircle className="close-popup" onClick={() => setOpenHouseRulesPopup(false)} />
            <h2>House Rules</h2>
            <div className="popup-scroll">
              <ul className="popup-amenities-list">
                <li>Check-in: 3 pm</li>
                <li>Check-out: 11 am</li>
                <li>Children: allowed</li>
                <li>Infants: allowed</li>
                <li>Pets: not allowed</li>
                <li>Parties and events: not allowed</li>
                <li>Smoking inside: not allowed</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListingPage;
